import { useState, useMemo } from 'react';
import type { Currency, Placement, Owner } from '../lib/types';
import type { ProjectedPlacement } from '../lib/forecast';
import { selectionHasOwner, isOwnerCredited, type Selection } from '../lib/splits';
import { effectiveEndDate } from '../lib/runners';
import { formatMoney, formatDate, formatDateShort } from '../lib/format';
import { ContractorDetail } from './ContractorDetail';

interface Props {
  projected: ProjectedPlacement[];
  viewerOid: string;
  selection: Selection;
  currency: Currency;
  /** Only contractors live on this date (post-renewal) are listed. */
  viewDate: string;
}

type SortKey =
  | 'name'
  | 'client'
  | 'cro'
  | 'consultant'
  | 'ao'
  | 'start'
  | 'end'
  | 'status'
  | 'wgp'
  | 'split';

export function ContractorTable({
  projected,
  viewerOid,
  selection,
  currency,
  viewDate,
}: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('end');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<Placement | null>(null);

  const visible = useMemo(() => {
    // Only placements where the current selection holds a credited role —
    // an ownership check rather than splitValue > 0 so £0-WGP placements
    // they own still list. Currency-routed (perm-desk) owners only count
    // when the deal's currency routes to the selected city.
    const rows = projected.filter(
      (pp) =>
        selectionHasOwner(pp.placement, selection) &&
        pp.placement.startDate <= viewDate &&
        pp.effectiveEnd >= viewDate,
    );
    const sorted = [...rows].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'end':
          return dir * a.effectiveEnd.localeCompare(b.effectiveEnd);
        case 'start':
          return dir * a.placement.startDate.localeCompare(b.placement.startDate);
        case 'split':
          return dir * (a.splitValue - b.splitValue);
        case 'wgp':
          return dir * (a.placement.wgp - b.placement.wgp);
        case 'client':
          return dir * (a.placement.clientName ?? '').localeCompare(b.placement.clientName ?? '');
        case 'cro':
          return dir * (a.placement.owners.cro?.name ?? '').localeCompare(b.placement.owners.cro?.name ?? '');
        case 'consultant':
          return dir * (a.placement.owners.consultant?.name ?? '').localeCompare(b.placement.owners.consultant?.name ?? '');
        case 'ao':
          return dir * (a.placement.owners.ao?.name ?? '').localeCompare(b.placement.owners.ao?.name ?? '');
        case 'status':
          return dir * stateBadge(a, viewDate).label.localeCompare(stateBadge(b, viewDate).label);
        case 'name':
          return (
            dir *
            (a.placement.candidateName ?? a.placement.jobTitle).localeCompare(
              b.placement.candidateName ?? b.placement.jobTitle,
            )
          );
      }
    });
    return sorted;
  }, [projected, sortKey, sortDir, selection, viewDate]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      // Money columns default to biggest-first; everything else ascending.
      setSortDir(key === 'split' || key === 'wgp' ? 'desc' : 'asc');
    }
  };

  return (
    <section>
      <div className="ds-sec">
        <h2>
          Contractors <em>on site {formatDate(viewDate)}.</em>
        </h2>
        <span>{visible.length} contractors · click a row for the placement</span>
      </div>

      <div className="ds-tw">
        <table className="ds-table">
          <thead>
            <tr>
              <ThSort label="Contractor" active={sortKey === 'name'} dir={sortDir} onClick={() => toggleSort('name')} />
              <ThSort label="Client" active={sortKey === 'client'} dir={sortDir} onClick={() => toggleSort('client')} />
              <ThSort label="CRO" active={sortKey === 'cro'} dir={sortDir} onClick={() => toggleSort('cro')} />
              <ThSort label="Consultant" active={sortKey === 'consultant'} dir={sortDir} onClick={() => toggleSort('consultant')} />
              <ThSort label="AO" active={sortKey === 'ao'} dir={sortDir} onClick={() => toggleSort('ao')} />
              <ThSort label="Start" active={sortKey === 'start'} dir={sortDir} onClick={() => toggleSort('start')} />
              <ThSort label="End" active={sortKey === 'end'} dir={sortDir} onClick={() => toggleSort('end')} />
              <ThSort label="Status" active={sortKey === 'status'} dir={sortDir} onClick={() => toggleSort('status')} />
              <ThSort label="WGP" active={sortKey === 'wgp'} dir={sortDir} onClick={() => toggleSort('wgp')} align="right" />
              <ThSort label="Split" active={sortKey === 'split'} dir={sortDir} onClick={() => toggleSort('split')} align="right" />
            </tr>
          </thead>
          <tbody>
            {visible.map((pp) => {
              const rowState = stateBadge(pp, viewDate);
              return (
                <tr key={pp.placement.id} onClick={() => setSelected(pp.placement)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="ds-name">
                      {pp.placement.candidateName ?? '—'}
                      <small>{pp.placement.jobTitle} · {pp.placement.idCode}</small>
                    </div>
                  </td>
                  <td className="trunc" style={{ color: 'var(--text-2)' }} title={pp.placement.clientName ?? undefined}>
                    {pp.placement.clientName ?? '—'}
                  </td>
                  <OwnerCell owner={pp.placement.owners.cro} placement={pp.placement} selection={selection} />
                  <OwnerCell owner={pp.placement.owners.consultant} placement={pp.placement} selection={selection} />
                  <OwnerCell owner={pp.placement.owners.ao} placement={pp.placement} selection={selection} />
                  <td className="num" style={{ color: 'var(--text-2)' }}>{formatDateShort(pp.placement.startDate)}</td>
                  <td className="num" style={{ color: 'var(--text-2)' }}>
                    {formatDateShort(pp.effectiveEnd)}
                    {(pp.renewed || pp.autoRenewed) && (
                      <span className="ds-pill perm" style={{ marginLeft: 8, minWidth: 0 }}>+6 mo</span>
                    )}
                  </td>
                  <td>
                    <span className={'ds-pill ' + rowState.pill} style={{ minWidth: 0 }}>{rowState.label}</span>
                  </td>
                  <td className="num" style={{ textAlign: 'right', color: 'var(--text-2)' }}>
                    {formatMoney(pp.placement.wgp, currency, true)}
                  </td>
                  <td className="num" style={{ textAlign: 'right', fontWeight: 600 }}>
                    {formatMoney(pp.splitValue, currency, true)}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>
                  No contractors on site on this date.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <ContractorDetail
          placement={selected}
          viewerOid={viewerOid}
          selection={selection}
          currency={currency}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

function OwnerCell({
  owner,
  placement,
  selection,
}: {
  owner: Owner | null;
  placement: Placement;
  selection: Selection;
}) {
  if (!owner) {
    return <td style={{ color: 'var(--text-4)' }}>—</td>;
  }
  // White when this owner's share is being counted in the current view
  // (including perm-desk owners routed in by the deal's currency).
  const credited = isOwnerCredited(owner, placement, selection);
  // Counted owners: bold + tick, not just brighter text (colour-blind safe).
  return (
    <td
      style={{ color: credited ? 'var(--text-1)' : 'var(--text-3)', fontWeight: credited ? 600 : 400 }}
      title={credited ? 'Share counted in this view' : 'Share not counted in this view'}
    >
      {credited && <span aria-label="counted" style={{ color: 'var(--rag-green-ink)', marginRight: 5 }}>✓</span>}
      {owner.name}
    </td>
  );
}

function ThSort({
  label,
  active,
  dir,
  onClick,
  align,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  align?: 'right';
}) {
  return (
    <th className={active ? 'sorted' : ''} style={align ? { textAlign: align } : undefined}>
      <button onClick={onClick}>
        {label}
        {active && <span style={{ color: 'var(--perm)', marginLeft: 4 }}>{dir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}

/**
 * Status ON the viewed date. A contractor inside their contract's own dates
 * is simply Live — the slider's renew/drop outcome only applies at the end
 * date, so it is not their status yet. Renewal badges appear only when the
 * view date is past the natural end and the row is alive purely because of
 * a projected (slider) or expected (Mercury) extension.
 */
function stateBadge(pp: ProjectedPlacement, viewDate: string) {
  const naturalEnd = effectiveEndDate(pp.placement);
  if (pp.placement.startDate <= viewDate && naturalEnd >= viewDate) {
    return { label: 'Live', pill: 'muted' };
  }
  if (pp.autoRenewed) {
    return { label: 'Auto-renew', pill: 'perm' };
  }
  if (pp.renewed) {
    return { label: 'Renewed', pill: 'perm' };
  }
  // Rows are filtered to live-at-viewDate, so this is a safety net only.
  return { label: 'Live', pill: 'muted' };
}
